import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Button,
  Select,
  Option,
} from "@mui/joy";
import { getUserSettings, saveThemePreference, saveLanguagePreference, UserSettings } from "../services/settingsService";

const Profile: React.FC = () => {
  const [displayName, setDisplayName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [userData, setUserData] = useState<any>(null);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Profile | Iskcon Kanpur";
  }, []);

  useEffect(() => {
    // Check authentication before loading profile
    const checkAuth = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/user/authcheck`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("User not authenticated");
        }
      } catch (err) {
        navigate("/signin", { replace: true });
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (!authLoading) {
      document.title = "Profile | Iskcon Kanpur";

      // Fetch user details from backend
      const fetchUserDetails = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_API_URL}/api/user/me`,
            {
              method: "GET",
              credentials: "include",
            }
          );

          if (!response.ok) {
            throw new Error("Failed to fetch user details");
          }

          const data = await response.json();
          setUserData(data);
          setDisplayName(data.display_name || "");
          setEmail(data.email || "");

          // Fetch user settings
          const userSettings = await getUserSettings();
          if (userSettings) {
            setSettings(userSettings);
          }
        } catch (err) {
          console.error(err);
          setError("Could not fetch user details.");
        } finally {
          setLoading(false);
        }
      };

      fetchUserDetails();
    }
  }, [authLoading]);

  if (authLoading || loading) {
    return <Loading message="Hare Krishna! Loading profile..." />;
  }

  return (
    <Box
      sx={{
        maxWidth: "500px",
        margin: "auto",
        padding: "20px",
        textAlign: "center",
      }}
    >
      <Typography level="h1" fontSize="1.8rem" sx={{ mb: 2 }}>
        Profile
      </Typography>
      <Typography level="body-md" sx={{ mb: 2 }}>
        Welcome to Iskcon Kanpur!
      </Typography>

      <Stack sx={{ gap: 4, mt: 2 }}>
        <form>
          <FormControl required sx={{ mb: 2 }}>
            <FormLabel>Full Name</FormLabel>
            <Input
              type="text"
              name="displayName"
              value={displayName}
              readOnly
            />
          </FormControl>
          <FormControl required sx={{ mb: 2 }}>
            <FormLabel>Email</FormLabel>
            <Input type="email" name="email" value={email} readOnly />
          </FormControl>
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Phone</FormLabel>
            <Input type="text" value={userData?.phone || "N/A"} readOnly />
          </FormControl>
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Role</FormLabel>
            <Input type="text" value={userData?.role || "N/A"} readOnly />
          </FormControl>
          
          {/* Theme Preference */}
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Theme</FormLabel>
            <Select
              value={settings?.theme || "system"}
              onChange={async (_, value) => {
                if (value) {
                  const success = await saveThemePreference(value as 'light' | 'dark' | 'system');
                  if (success) {
                    setSettings(prev => prev ? { ...prev, theme: value as any } : null);
                    setMessage("Theme updated successfully!");
                    setTimeout(() => setMessage(null), 3000);
                  } else {
                    setError("Failed to update theme");
                    setTimeout(() => setError(null), 3000);
                  }
                }
              }}
            >
              <Option value="light">Light</Option>
              <Option value="dark">Dark</Option>
              <Option value="system">System</Option>
            </Select>
          </FormControl>

          {/* Language Preference */}
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Language</FormLabel>
            <Select
              value={settings?.language || "en"}
              onChange={async (_, value) => {
                if (value) {
                  const success = await saveLanguagePreference(value);
                  if (success) {
                    setSettings(prev => prev ? { ...prev, language: value } : null);
                    setMessage("Language updated successfully!");
                    setTimeout(() => setMessage(null), 3000);
                  } else {
                    setError("Failed to update language");
                    setTimeout(() => setError(null), 3000);
                  }
                }
              }}
            >
              <Option value="en">English</Option>
              <Option value="hi">Hindi (हिंदी)</Option>
            </Select>
          </FormControl>
        </form>
        {message && (
          <Typography level="body-sm" sx={{ color: "green", mt: 2 }}>
            {message}
          </Typography>
        )}
        {error && (
          <Typography level="body-sm" sx={{ color: "red", mt: 2 }}>
            {error}
          </Typography>
        )}
        <Button
          type="button"
          onClick={async () => {
            try {
              const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/auth/logout`,
                {
                  method: "POST",
                  credentials: "include",
                }
              );

              if (response.ok) {
                navigate("/signin", { replace: true });
              } else {
                throw new Error("Logout failed");
              }
            } catch (err) {
              console.error("Logout error:", err);
              setError("Failed to log out.");
            }
          }}
          fullWidth
          color="danger"
          sx={{ mt: 3 }}
        >
          Logout
        </Button>
      </Stack>
    </Box>
  );
};

export default Profile;
